import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { useGraphql } from 'utils/hooks/useGraphql';
import { isNonEmptyArray, isNotNull } from 'utils/tsHelpers';

import { TEMPLATE_METADATA_FRAGMENT } from './constants';
import { TemplateMetadata } from './types';

const createQueryForTemplateId = (templateId: number) =>
  `template_${templateId}:template(id:${templateId}) {
    ...TemplateMetadata
  }
`;

/**
 *
 * Note: "createQueryForTemplateIds" won't create a valid query if templateIds is empty.
 * We also can't skip the gql call unless the query itself is valid.
 * To resolve this we can pass an array with an psuedo id (ie: [0]) to satisfy the query in order to skip
 * the gql call. The actual ids doesn't really matter since it will be skipped.
 */

const createQueryForTemplateIds = (templateIds: number[]) => gql`
  ${TEMPLATE_METADATA_FRAGMENT}
  query TemplatesById {
    ${templateIds.sort().map(createQueryForTemplateId).join('\n')}
  }
`;

const emptyQueryIds = [0];

export const useTemplatesByIds = (templateIds: number[]) => {
  const query = createQueryForTemplateIds(
    isNonEmptyArray(templateIds) ? templateIds : emptyQueryIds,
  );

  const {
    data: results,
    error: didError,
    isLoading,
  } = useGraphql<Record<`template_${number}`, TemplateMetadata | null>>(query, {
    skip: !isNonEmptyArray(templateIds),
  });

  const templates = useMemo(() => Object.values(results ?? {}).filter(isNotNull), [results]);
  return { templates, didError, isLoading };
};
